<script setup>
	const approachPoints = [
		"Senior-led from day one",
		"Clean, tested, maintainable code",
		"Jargon-free, over-communicated updates",
		"Transparent scope, timeline and pricing",
	];

	const stats = [
		{ figure: "20+", label: "years building and rescuing software" },
		{ figure: "Senior-led", label: "on every single engagement" },
		{ figure: "Human", label: "real engineers, not AI guesswork" },
	];

	const values = [
		{ icon: "📣", title: "Over-communication", text: "We'd rather update you too much than leave you guessing." },
		{ icon: "🤝", title: "Respect & courtesy", text: "Every interaction matters. We treat clients and teammates with respect." },
		{ icon: "🎯", title: "Discipline", text: "Consistency and focus keep projects on track and high quality." },
		{ icon: "📚", title: "Always learning", text: "We stay curious and keep sharpening our craft — never coasting." },
	];
</script>

<template>
	<div class="about">
		<section class="hero">
			<div class="hero-inner">
				<span class="offer-pill">Software consultancy</span>
				<h1 class="hero-title">We fix, build, and ship software that lasts</h1>
				<p class="hero-lead">
					Lucentary is a software consultancy. For two decades we've architected and rescued
					real systems — now we help small businesses clean up AI-built messes and turn shaky
					code into something solid.
				</p>
				<div class="hero-actions">
					<RouterLink :to="{ name: 'contact' }" class="btn btn-primary">Book a call</RouterLink>
					<RouterLink :to="{ name: 'services' }" class="btn btn-ghost">See pricing</RouterLink>
				</div>
			</div>
		</section>

		<section class="approach">
			<div class="approach-inner">
				<div class="approach-copy">
					<div class="accent"></div>
					<h2 class="approach-heading">Real engineers, honest advice</h2>
					<p class="approach-text">
						Every engagement is led by a senior engineer who has actually shipped production
						software. We tell you the truth about what's worth building, fixing, or leaving
						alone — in plain English, with no surprises.
					</p>
				</div>
				<div class="approach-card">
					<h3 class="approach-card-title">How we work</h3>
					<ul class="approach-list">
						<li v-for="point in approachPoints" :key="point">{{ point }}</li>
					</ul>
				</div>
			</div>
		</section>

		<section class="stats">
			<div class="stats-inner">
				<div v-for="stat in stats" :key="stat.label" class="stat">
					<div class="stat-figure">{{ stat.figure }}</div>
					<div class="stat-label">{{ stat.label }}</div>
				</div>
			</div>
		</section>

		<section class="values">
			<div class="values-inner">
				<header class="block-header">
					<span class="block-eyebrow">Our values</span>
					<h2 class="block-heading">What we stand for</h2>
				</header>
				<div class="values-grid">
					<article v-for="value in values" :key="value.title" class="value">
						<div class="value-icon" aria-hidden="true">{{ value.icon }}</div>
						<h3 class="value-title">{{ value.title }}</h3>
						<p class="value-text">{{ value.text }}</p>
					</article>
				</div>
			</div>
		</section>

		<section class="cta">
			<div class="cta-inner">
				<div class="cta-card">
					<h2 class="cta-title">Let's build something solid.</h2>
					<p class="cta-lead">Tell us what you're working on and we'll recommend the right approach, with a clear plan and no jargon.</p>
					<div class="cta-actions">
						<RouterLink :to="{ name: 'contact' }" class="btn btn-on-blue">Book a call</RouterLink>
						<RouterLink :to="{ name: 'services' }" class="btn btn-ghost-light">See pricing</RouterLink>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<style scoped>
	.about {
		--bg: #f5f9fd;
		--surface: #ffffff;
		--surface-muted: #eef4fb;
		--border: #e2ecf6;
		--border-strong: #cddcec;
		--navy: #0f2742;
		--ink: #16314f;
		--ink-soft: #4a5d75;
		--ink-muted: #7589a1;
		--sky: #0ea5e9;
		--sky-deep: #0284c7;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dceafe;

		font-family: "Poppins", "Plus Jakarta Sans", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
		color: var(--ink);
		background: var(--bg);
		overflow-x: hidden;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.95rem 1.9rem;
		border-radius: 12px;
		font-family: inherit;
		font-size: 1.05rem;
		font-weight: 700;
		text-decoration: none;
		cursor: pointer;
		border: 1px solid transparent;
		transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 14px 28px -14px rgba(37, 99, 235, 0.7);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 20px 34px -16px rgba(37, 99, 235, 0.75);
	}

	.btn-ghost {
		background: var(--surface);
		color: var(--ink);
		border-color: var(--border-strong);
	}

	.btn-ghost:hover {
		transform: translateY(-2px);
		border-color: var(--blue);
		color: var(--blue-deep);
	}

	.btn-on-blue {
		background: #ffffff;
		color: var(--blue-deep);
		box-shadow: 0 14px 30px -14px rgba(0, 0, 0, 0.5);
	}

	.btn-on-blue:hover {
		transform: translateY(-2px);
		background: #f1f6ff;
		color: var(--blue-deep);
		box-shadow: 0 20px 36px -16px rgba(0, 0, 0, 0.55);
	}

	.btn-ghost-light {
		background: transparent;
		color: #ffffff;
		border-color: rgba(255, 255, 255, 0.45);
	}

	.btn-ghost-light:hover {
		transform: translateY(-2px);
		background: rgba(255, 255, 255, 0.12);
		border-color: #ffffff;
	}

	.hero {
		padding: clamp(3.5rem, 8vw, 6rem) 1.5rem clamp(2.5rem, 5vw, 4rem);
	}

	.hero-inner {
		max-width: 920px;
		margin: 0 auto;
		text-align: center;
	}

	.offer-pill {
		display: inline-block;
		padding: 0.5rem 1.15rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.92rem;
		font-weight: 700;
		margin-bottom: 1.4rem;
	}

	.hero-title {
		font-size: clamp(2.2rem, 4.2vw, 3.4rem);
		line-height: 1.08;
		font-weight: 800;
		letter-spacing: -0.025em;
		color: var(--navy);
		margin: 0 0 1.25rem;
	}

	.hero-lead {
		font-size: clamp(1.08rem, 1.4vw, 1.28rem);
		line-height: 1.65;
		color: var(--ink-soft);
		max-width: 60ch;
		margin: 0 auto 2rem;
	}

	.hero-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
	}

	.approach {
		padding: clamp(3rem, 7vw, 5.5rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.approach-inner {
		max-width: 1140px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: 1.05fr 0.95fr;
		gap: clamp(2rem, 5vw, 4rem);
		align-items: center;
	}

	.accent {
		width: 56px;
		height: 6px;
		border-radius: 999px;
		background: var(--blue);
		margin-bottom: 1.1rem;
	}

	.approach-heading {
		font-size: clamp(1.7rem, 2.8vw, 2.4rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		color: var(--navy);
		margin: 0 0 1rem;
	}

	.approach-text {
		font-size: clamp(1.05rem, 1.3vw, 1.2rem);
		line-height: 1.7;
		color: var(--ink-soft);
		margin: 0;
	}

	.approach-card {
		padding: clamp(1.85rem, 3vw, 2.5rem);
		border-radius: 20px;
		background: var(--bg);
		border: 1px solid var(--border);
		box-shadow: 0 34px 64px -42px rgba(15, 39, 66, 0.45);
	}

	.approach-card-title {
		font-size: 1.2rem;
		font-weight: 700;
		color: var(--navy);
		margin: 0 0 1.1rem;
	}

	.approach-list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 0.8rem;
	}

	.approach-list li {
		position: relative;
		padding-left: 1.8rem;
		font-size: 1.02rem;
		line-height: 1.5;
		color: var(--ink);
		font-weight: 500;
	}

	.approach-list li::before {
		content: "✓";
		position: absolute;
		left: 0;
		top: 0.05rem;
		width: 1.3rem;
		height: 1.3rem;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.78rem;
		font-weight: 800;
	}

	.stats {
		padding: clamp(2.5rem, 5vw, 3.75rem) 1.5rem;
		background: var(--bg);
	}

	.stats-inner {
		max-width: 1000px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 1.25rem;
	}

	.stat {
		padding: 1.65rem 1.4rem;
		border: 1px solid var(--border);
		border-radius: 16px;
		background: var(--surface);
		text-align: center;
	}

	.stat-figure {
		font-size: clamp(1.6rem, 2.4vw, 2.1rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		color: var(--blue-deep);
	}

	.stat-label {
		margin-top: 0.35rem;
		color: var(--ink-soft);
		font-size: 0.98rem;
		line-height: 1.45;
		font-weight: 500;
	}

	.values {
		padding: clamp(3rem, 7vw, 5.5rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
	}

	.values-inner {
		max-width: 1140px;
		margin: 0 auto;
	}

	.block-header {
		text-align: center;
		margin-bottom: 2.75rem;
	}

	.block-eyebrow {
		display: inline-block;
		font-size: 0.85rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.7rem;
	}

	.block-heading {
		font-size: clamp(1.85rem, 3vw, 2.7rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		color: var(--navy);
		margin: 0;
	}

	.values-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 1.25rem;
	}

	.value {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 1.65rem 1.45rem;
		border: 1px solid var(--border);
		border-radius: 16px;
		background: var(--bg);
		transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
	}

	.value:hover {
		transform: translateY(-4px);
		border-color: var(--blue);
		box-shadow: 0 24px 44px -30px rgba(15, 39, 66, 0.45);
	}

	.value-icon {
		font-size: 1.6rem;
		line-height: 1;
		margin-bottom: 0.75rem;
	}

	.value-title {
		font-size: 1.08rem;
		font-weight: 700;
		color: var(--navy);
		margin: 0 0 0.4rem;
	}

	.value-text {
		color: var(--ink-soft);
		font-size: 0.96rem;
		line-height: 1.55;
		margin: 0;
	}

	.cta {
		padding: clamp(3rem, 6vw, 5rem) 1.5rem;
		background: var(--bg);
	}

	.cta-inner {
		max-width: 1000px;
		margin: 0 auto;
	}

	.cta-card {
		padding: clamp(3rem, 5vw, 4.25rem) clamp(1.75rem, 4vw, 4rem);
		border-radius: 24px;
		background: linear-gradient(135deg, var(--blue) 0%, var(--sky-deep) 100%);
		color: #ffffff;
		text-align: center;
		box-shadow: 0 34px 64px -36px rgba(37, 99, 235, 0.7);
	}

	.cta-title {
		font-size: clamp(1.9rem, 3.5vw, 2.8rem);
		font-weight: 800;
		line-height: 1.1;
		letter-spacing: -0.02em;
		margin: 0 auto 1.25rem;
		max-width: 20ch;
		color: #ffffff;
	}

	.cta-lead {
		font-size: clamp(1.08rem, 1.4vw, 1.25rem);
		line-height: 1.6;
		color: rgba(255, 255, 255, 0.92);
		max-width: 52ch;
		margin: 0 auto 2rem;
	}

	.cta-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
	}

	@media (max-width: 900px) {
		.approach-inner { grid-template-columns: 1fr; }

		.values-grid { grid-template-columns: repeat(2, 1fr); }

		.stats-inner { grid-template-columns: 1fr; }
	}

	@media (max-width: 640px) {
		.hero-actions,
		.cta-actions { flex-direction: column; align-items: stretch; }

		.btn { width: 100%; }

		.values-grid { grid-template-columns: 1fr; }
	}
</style>